import React, { useState } from 'react';
import FileUpload from './FileUpload';
import SafeIcon from '../common/SafeIcon';
import { FaCamera, FaEnvelope, FaCheckCircle, FaUserShield, FaExclamationTriangle } from 'react-icons/fa';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const QuickUpload = () => {
  const [email, setEmail] = useState('');
  const [driverFiles, setDriverFiles] = useState([]);
  const [passengerPayment, setPassengerPayment] = useState('');
  const [currency, setCurrency] = useState('GBP');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referenceCode, setReferenceCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) newErrors.email = 'Email is required';
    else if (!emailRegex.test(email)) newErrors.email = 'Please enter a valid email address';

    if (!driverFiles || driverFiles.length === 0) {
      newErrors.driverFiles = 'Please upload 1 driver pay screenshot';
    } else if (driverFiles.length > 1) {
      newErrors.driverFiles = 'Please upload only 1 screenshot.';
    }

    if (!consent) newErrors.consent = 'Please provide consent to use your information';

    // Optional: validate passengerPayment numeric if provided
    if (passengerPayment !== '' && Number.isNaN(Number(passengerPayment))) {
      newErrors.passengerPayment = 'Passenger payment must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const submitData = new FormData();
      submitData.append('email', email.trim());
      submitData.append('currency', currency);
      submitData.append('consent', String(consent));

      // Keep optional empty if not provided
      if (passengerPayment !== '') submitData.append('passengerPayment', passengerPayment);

      // Enforce single screenshot
      submitData.append('driverFiles', driverFiles[0]);

      const res = await fetch(`${API_BASE_URL}/api/evidence`, {
        method: 'POST',
        body: submitData,
      });

      if (!res.ok) {
        let msg = 'Failed to submit evidence. Please try again.';
        try {
          const body = await res.json();
          if (body?.message) msg = body.message;
          if (body?.errors && typeof body.errors === 'object') {
            // Map backend field errors to frontend
            setErrors(body.errors);
            throw new Error('validation');
          }
        } catch (_) {
          // ignore json parse errors
        }
        throw new Error(msg);
      }

      const data = await res.json();
      setReferenceCode(data.referenceCode);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Fetch error details:', error);
      if (error.message === 'validation') return;
      setErrors({ submit: error.message || 'Failed to submit evidence. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="text-center py-12 bg-white shadow-lg p-8">
        <div className="w-20 h-20 bg-green-100 flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FaCheckCircle} className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-black mb-3">Thank You! 🎉</h2>
        <p className="text-lg text-gray-600 mb-6">Your evidence has been received by WIE International</p>
        <div className="bg-gray-50 p-6 mb-6">
          <p className="text-sm font-semibold text-gray-700 mb-2">Your Reference Code:</p>
          <p className="font-mono text-2xl font-bold text-blue-600">{referenceCode}</p>
          <p className="text-xs text-gray-500 mt-2">Save this code for your records</p>
        </div>
        <div className="flex items-center justify-center space-x-2 text-green-600 bg-green-50 p-4">
          <SafeIcon icon={FaUserShield} className="w-5 h-5" />
          <span className="text-sm font-medium">Your information is safe with us</span>
        </div>
      </div>
    );
  }

  const currencySymbols = { EUR: '€', GBP: '£' };

  return (
    <div className="bg-white shadow-lg p-6" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Hero Section */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-black flex items-center justify-center mx-auto mb-4">
          <SafeIcon icon={FaCamera} className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-black mb-3">Help us uncover Uber’s hidden commission rate</h1>
        <p className="text-lg text-gray-600 mb-2">Show us what Uber paid you vs. what the passenger paid</p>
        <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 bg-gray-100 p-3">
          <span className="font-medium">📸 Upload screenshots • 📧 Enter email • ✅ Submit</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Email Section */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <SafeIcon icon={FaEnvelope} className="w-5 h-5 text-black" />
            <label htmlFor="email" className="text-lg font-bold text-black">Your Email Address</label>
            <span className="text-red-600 font-bold">*</span>
          </div>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-4 py-4 border-2 text-lg ${
              errors.email ? 'border-red-600 bg-red-50' : 'border-black'
            } focus:outline-none focus:ring-2 focus:ring-red-600 transition-all`}
            placeholder="your.email@example.com"
            style={{ fontFamily: 'Arial, sans-serif' }}
          />
          {errors.email ? (
            <p className="text-red-600 text-sm mt-2 font-medium">⚠️ {errors.email}</p>
          ) : (
            <p className="text-sm text-gray-500 mt-2">We will only email you about your evidence</p>
          )}
        </div>

        {/* Driver Pay File Upload Section */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <SafeIcon icon={FaCamera} className="w-5 h-5 text-black" />
            <label className="text-lg font-bold text-black">Upload Your Pay Screenshots</label>
            <span className="text-red-600 font-bold">*</span>
          </div>
          <FileUpload
            files={driverFiles}
            onFilesChange={setDriverFiles}
            error={errors.driverFiles}
            maxFiles={1}
            instructions={[
              'Uber app showing your pay amount',
              'Trip details and promised pay',
              'Any messages about pay changes'
            ]}
            tip="If you have multiple screenshots, pick the one showing the clearest pay amount."
          />
        </div>

        {/* Passenger Payment Section */}
        <div className="bg-gray-50 p-6 border border-gray-200">
          <div className="flex items-center space-x-2 mb-4">
            <label className="text-lg font-bold text-black">Passenger Payment Details</label>
            <span className="text-gray-500 text-sm font-normal">(Optional)</span>
          </div>

          {/* Privacy Warning */}
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex items-start">
              <SafeIcon icon={FaExclamationTriangle} className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-yellow-800">Privacy Caution</p>
                <p className="text-sm text-yellow-700 mt-1">
                  Please ensure <span className="font-bold">passenger journey details</span> are not visible or are blurred out. Focus only on the price.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {/* Passenger Payment Input */}
            <div>
              <label htmlFor="passengerPayment" className="block text-sm font-medium text-gray-700 mb-2">
                Amount Passenger Paid
              </label>
              <div className="flex space-x-3">
                <div className="relative flex-1">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                    <span className="text-gray-500 text-lg">{currencySymbols[currency]}</span>
                  </div>
                  <input
                    type="number"
                    id="passengerPayment"
                    value={passengerPayment}
                    onChange={(e) => setPassengerPayment(e.target.value)}
                    className={`w-full pl-8 pr-4 py-4 border-2 text-lg ${
                      errors.passengerPayment ? 'border-red-600 bg-red-50' : 'border-black'
                    } focus:outline-none focus:ring-2 focus:ring-red-600 transition-all`}
                    placeholder="e.g., 25.50"
                    step="0.01"
                    style={{ fontFamily: 'Arial, sans-serif' }}
                  />
                </div>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="px-4 py-4 border-2 border-black text-lg focus:outline-none focus:ring-2 focus:ring-red-600 transition-all bg-white"
                  style={{ fontFamily: 'Arial, sans-serif' }}
                >
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              {errors.passengerPayment && (
                <p className="text-red-600 text-sm mt-2 font-medium">⚠️ {errors.passengerPayment}</p>
              )}
            </div>

          </div>
        </div>

        {/* Consent Section */}
        <div className="bg-gray-100 p-6">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-5 w-5 text-red-600 focus:ring-red-600 border-gray-300"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              <span className="font-bold text-black">I agree:</span> WIE International can use my screenshots and information
              to help with the Dynamic Pay Claim and WIE's wider work protecting platform worker rights. They can email me about this case.
              <span className="text-red-600 font-bold"> *</span>
            </span>
          </label>
          {errors.consent && (
            <p className="text-red-600 text-sm mt-3 font-medium">⚠️ {errors.consent}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-red-600 text-white text-lg font-bold py-4 px-6 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all"
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending your evidence...
            </span>
          ) : (
            <span>📤 Send My Details</span>
          )}
        </button>

        {errors.submit && (
          <div className="bg-red-50 border-2 border-red-600 p-4">
            <p className="text-red-600 font-medium text-center">⚠️ {errors.submit}</p>
          </div>
        )}
      </form>
    </div>
  );
};

export default QuickUpload;